/** 入出金の手入力(1件)。予定・確定のどちらでも登録できる */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import { formatAmountInput, parseAmount } from '@core/lib/money';
import { createManualEntry } from './cashRepo';
import type { CashAccount } from '@core/lib/types';

export default function ManualEntryForm({
  accounts,
  defaultAccountId,
  onClose,
  onSaved,
}: {
  accounts: CashAccount[];
  defaultAccountId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [cashAccountId, setCashAccountId] = useState(defaultAccountId);
  const [date, setDate] = useState(todayStr());
  const [direction, setDirection] = useState<'in' | 'out'>('out');
  const [status, setStatus] = useState<'planned' | 'settled'>('settled');
  const [amountText, setAmountText] = useState('');
  const [description, setDescription] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!user) return;
    const amount = parseAmount(amountText);
    if (amount === null) {
      toast('金額を正しく入力してください(1円以上の整数)');
      return;
    }
    setBusy(true);
    try {
      await createManualEntry(
        { cashAccountId, date, direction, amount, description, counterparty, status },
        user.id,
      );
      toast(status === 'planned' ? '入出金の予定を登録しました' : '入出金を登録しました');
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(640px, 92vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3>入出金の手入力</h3>
      </div>

      <div className="form-grid">
        <div>
          <label>口座</label>
          <select value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>日付</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label>区分</label>
          <select value={direction} onChange={(e) => setDirection(e.target.value as 'in' | 'out')}>
            <option value="in">入金</option>
            <option value="out">出金</option>
          </select>
        </div>
        <div>
          <label>状態</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as 'planned' | 'settled')}>
            <option value="settled">確定(実際に動いた)</option>
            <option value="planned">予定(これから動く)</option>
          </select>
        </div>
        <div>
          <label>金額(円)</label>
          <input
            inputMode="numeric"
            value={amountText}
            onChange={(e) => setAmountText(formatAmountInput(e.target.value))}
            placeholder="120,000"
          />
        </div>
        <div>
          <label>取引先(任意)</label>
          <input value={counterparty} onChange={(e) => setCounterparty(e.target.value)} />
        </div>
        <div className="fw">
          <label>内容</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="例)〇〇商事 8月分入金"
          />
        </div>
      </div>

      <button className="btn-green" onClick={() => void save()} disabled={busy}>
        {busy ? '登録中…' : '登録する'}
      </button>
      <p className="hint" style={{ marginTop: 10 }}>
        「予定」で登録した行は、CSV取込のときに実際の入出金と突合(消込)されます。
      </p>
    </Modal>
  );
}
